/**
 * Judge Queue — Review pending lift attempts and cast votes.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, RefreshControl, Alert, Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import PaywallGate from '@/components/PaywallGate';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { getPendingReviews, castVote, type ReasonCode, type VoteType } from '@/lib/judging';
import { supabase } from '@/lib/supabase';
import { ArrowLeft, Play, CircleCheck as WhiteLight, CircleX as RedLight, UserPlus, Shield } from 'lucide-react-native';

const REASON_CODES: { code: ReasonCode; label: string }[] = [
  { code: 'depth', label: 'Depth' },
  { code: 'lockout', label: 'Lockout' },
  { code: 'rack_command', label: 'Rack command' },
  { code: 'press_command', label: 'Press command' },
  { code: 'down_command', label: 'Down command' },
  { code: 'hitching', label: 'Hitching' },
  { code: 'ramping', label: 'Ramping' },
  { code: 'soft_knees', label: 'Soft knees' },
  { code: 'butt_off_bench', label: 'Butt off bench' },
  { code: 'downward_movement', label: 'Downward movement' },
  { code: 'belt_support', label: 'Belt support' },
  { code: 'heaving', label: 'Heaving' },
  { code: 'incomplete_rep', label: 'Incomplete rep' },
];

export default function JudgeQueueScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const isCoach = hasAtLeast(tier, 'coach_pro');
  const isClub = hasAtLeast(tier, 'org_pro');
  const gymId = profile?.gym_id;

  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [gymJudges, setGymJudges] = useState<any[]>([]);

  const loadGymJudges = useCallback(async () => {
    if (!isClub || !gymId) return;
    const { data } = await supabase
      .from('judges')
      .select('id, profile_id, certified_at, profiles(display_name, handle)')
      .eq('gym_id', gymId);
    setGymJudges(data ?? []);
  }, [isClub, gymId]);

  const load = useCallback(async () => {
    const data = await getPendingReviews(20);
    setReviews(data);
    setLoading(false);
    setRefreshing(false);
    loadGymJudges();
  }, [loadGymJudges]);

  useEffect(() => { load(); }, [load]);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>JUDGE QUEUE</Text>
      </View>

      {!isCoach ? (
        <View style={styles.content}>
          <PaywallGate requiredTier="coach_pro" title="Judge Certification" body="Become a certified lift judge with a Coach subscription.">
            <></>
          </PaywallGate>
        </View>
      ) : (
      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} tintColor={c.accent} />}
      >
        {/* Club: Manage Judges Panel */}
        {isClub && gymId && (
          <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, padding: 16, gap: 12, marginBottom: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Shield size={18} color={c.accent} />
              <Text style={{ color: c.text, fontFamily: TYPO.heading.bold, fontSize: 15, flex: 1 }}>MANAGE JUDGES</Text>
              <Text style={{ color: c.textSecondary, fontSize: 12 }}>{gymJudges.length} judge{gymJudges.length !== 1 ? 's' : ''}</Text>
            </View>
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.accent, paddingVertical: 10, paddingHorizontal: 16, borderRadius: 4, alignSelf: 'flex-start' }}
              onPress={() => Alert.alert('Invite Judge', 'Send judge certification invite to a gym member.')}
            >
              <UserPlus size={14} color="#000" />
              <Text style={{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 12 }}>INVITE JUDGE</Text>
            </TouchableOpacity>
            {gymJudges.length > 0 && (
              <View style={{ gap: 6 }}>
                {gymJudges.map((j: any) => (
                  <View key={j.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}>
                    <Shield size={12} color={c.accent} />
                    <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.body.semibold, flex: 1 }}>
                      {j.profiles?.display_name || j.profiles?.handle || 'Unknown'}
                    </Text>
                    <Text style={{ color: c.textTertiary, fontSize: 10 }}>
                      {j.certified_at ? new Date(j.certified_at).toLocaleDateString() : ''}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {loading ? (
          <ActivityIndicator color={c.accent} style={{ padding: 40 }} />
        ) : reviews.length === 0 ? (
          <View style={[styles.empty, { borderColor: c.border }]}>
            <WhiteLight size={32} color={c.textTertiary} />
            <Text style={[styles.emptyTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
              NO PENDING REVIEWS
            </Text>
            <Text style={[styles.emptyDesc, { color: c.textTertiary }]}>
              All lift attempts have been judged. Check back soon!
            </Text>
          </View>
        ) : (
          reviews.map((item) => (
            <AttemptCard
              key={item.id}
              item={item}
              colors={c}
              profileId={profile?.id}
              onVoted={load}
            />
          ))
        )}
      </ScrollView>
      )}
    </SafeAreaView>
  );
}

function AttemptCard({ item, colors: c, profileId, onVoted }: { item: any; colors: any; profileId?: string; onVoted: () => void }) {
  const attempt = item.lift_attempts;
  const lifterName = attempt?.profiles?.display_name || attempt?.profiles?.handle || 'Unknown';

  const [vote, setVote] = useState<VoteType | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<ReasonCode[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleReason = (code: ReasonCode) => {
    setSelectedReasons(prev =>
      prev.includes(code) ? prev.filter(r => r !== code) : [...prev, code]
    );
  };

  const handleSubmit = async () => {
    if (!vote || !profileId || !attempt?.id) return;
    if (vote === 'red' && selectedReasons.length === 0) {
      Alert.alert('Reason required', 'Select at least one reason code for a red vote.');
      return;
    }
    setSubmitting(true);
    const ok = await castVote(attempt.id, profileId, vote, vote === 'red' ? selectedReasons : []);
    setSubmitting(false);
    if (ok) {
      setSubmitted(true);
      onVoted();
    } else {
      Alert.alert('Oops', "Couldn't submit your vote. Please try again.");
    }
  };

  if (submitted) {
    return (
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, alignItems: 'center', padding: 20 }]}>
        <Text style={[{ color: c.success || '#22c55e', fontFamily: TYPO.heading.bold, fontSize: 14 }]}>
          Vote submitted ✓
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      {/* Lifter info */}
      <View style={styles.attemptInfo}>
        <Text style={[styles.lifterName, { color: c.text, fontFamily: TYPO.heading.bold }]}>{lifterName}</Text>
        <View style={styles.attemptMeta}>
          <Text style={[styles.metaText, { color: c.textSecondary }]}>
            {attempt?.lift_key?.replace(/_/g, ' ').toUpperCase()} · {attempt?.weight_kg}kg · Attempt #{attempt?.attempt_number}
          </Text>
        </View>
      </View>

      {/* Video */}
      {item.video_url ? (
        <TouchableOpacity
          style={[styles.videoPlaceholder, { backgroundColor: c.surfaceVariant || c.surface, borderColor: c.border }]}
          onPress={() => Linking.openURL(item.video_url)}
        >
          <Play size={24} color={c.accent} />
          <Text style={[{ color: c.accent, fontSize: 12, fontFamily: TYPO.body.semibold }]}>Watch Video</Text>
        </TouchableOpacity>
      ) : (
        <View style={[styles.videoPlaceholder, { backgroundColor: c.surfaceVariant || c.surface, borderColor: c.border }]}>
          <Text style={[{ color: c.textTertiary, fontSize: 12 }]}>No video</Text>
        </View>
      )}

      {/* Judge Lights */}
      <View style={styles.lightsRow}>
        <TouchableOpacity
          style={[styles.lightBtn, { backgroundColor: vote === 'white' ? '#ffffff' : c.surface, borderColor: vote === 'white' ? '#ffffff' : c.border }]}
          onPress={() => { setVote('white'); setSelectedReasons([]); }}
        >
          <WhiteLight size={28} color={vote === 'white' ? '#000' : c.textTertiary} />
          <Text style={[styles.lightLabel, { color: vote === 'white' ? '#000' : c.textTertiary, fontFamily: TYPO.heading.bold }]}>WHITE</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.lightBtn, { backgroundColor: vote === 'red' ? '#ef4444' : c.surface, borderColor: vote === 'red' ? '#ef4444' : c.border }]}
          onPress={() => setVote('red')}
        >
          <RedLight size={28} color={vote === 'red' ? '#fff' : c.textTertiary} />
          <Text style={[styles.lightLabel, { color: vote === 'red' ? '#fff' : c.textTertiary, fontFamily: TYPO.heading.bold }]}>RED</Text>
        </TouchableOpacity>
      </View>

      {/* Reason codes (red only) */}
      {vote === 'red' && (
        <View style={styles.reasonsWrap}>
          <Text style={[styles.reasonsTitle, { color: c.textSecondary, fontFamily: TYPO.body.semibold }]}>Reason codes:</Text>
          <View style={styles.reasonsGrid}>
            {REASON_CODES.map(({ code, label }) => {
              const active = selectedReasons.includes(code);
              return (
                <TouchableOpacity
                  key={code}
                  style={[styles.reasonChip, { backgroundColor: active ? '#ef4444' + '30' : c.surface, borderColor: active ? '#ef4444' : c.border }]}
                  onPress={() => toggleReason(code)}
                >
                  <Text style={[styles.reasonChipText, { color: active ? '#ef4444' : c.textSecondary }]}>{label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      )}

      {/* Submit */}
      {vote && (
        <TouchableOpacity
          style={[styles.submitBtn, { backgroundColor: c.accent }]}
          onPress={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={[styles.submitText, { color: '#000', fontFamily: TYPO.heading.bold }]}>SUBMIT VOTE</Text>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 },
  backBtn: { padding: 4 },
  title: { fontSize: FONT.xl, letterSpacing: 1 },
  content: { padding: 16, gap: SPACE.lg, paddingBottom: 40 },
  empty: { padding: 40, alignItems: 'center', gap: SPACE.md, borderWidth: 1, borderRadius: RADIUS.xs, borderStyle: 'dashed' },
  emptyTitle: { fontSize: 14, letterSpacing: 1 },
  emptyDesc: { fontSize: 12, textAlign: 'center', lineHeight: 18 },
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.md },
  attemptInfo: { gap: 4 },
  lifterName: { fontSize: FONT.base },
  attemptMeta: {},
  metaText: { fontSize: 12 },
  videoPlaceholder: { borderWidth: 1, borderRadius: RADIUS.xs, paddingVertical: 30, alignItems: 'center', justifyContent: 'center', gap: 6 },
  lightsRow: { flexDirection: 'row', gap: SPACE.md },
  lightBtn: { flex: 1, borderWidth: 2, borderRadius: RADIUS.xs, paddingVertical: 16, alignItems: 'center', gap: 6 },
  lightLabel: { fontSize: 12, letterSpacing: 1 },
  reasonsWrap: { gap: SPACE.xs },
  reasonsTitle: { fontSize: 12 },
  reasonsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  reasonChip: { borderWidth: 1, borderRadius: RADIUS.xs, paddingHorizontal: 10, paddingVertical: 6 },
  reasonChipText: { fontSize: 11 },
  submitBtn: { paddingVertical: 14, alignItems: 'center', borderRadius: RADIUS.xs },
  submitText: { fontSize: 14, letterSpacing: 1 },
});
